import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate, useParams } from 'react-router-dom';
import { CircleAlert, Clock, UserCheck, type LucideIcon } from 'lucide-react';
import { InvitationStatus } from '@shopify-multiseller/graphql/partner/types';
import { ErrorPage } from '../../components/ErrorPage/ErrorPage';
import { INVITATION_REMEDY } from './AcceptInvitation';
import { ResultCard } from '../../components/ResultCard';

import { BlankLoadingPage } from '../../views/BlankLoadingPage/BlankLoadingPage';

/**
 * What to say for each way an invitation can fail.
 *
 * Every case previously produced "The invitation link is invalid/expired",
 * which covers three different situations with three different next steps:
 * ask the shop for a new invitation, sign in with the account you already
 * made, or open the link from the email rather than a copy of it.
 */
const OUTCOMES: Record<
    Exclude<InvitationStatus, InvitationStatus.Valid>,
    { icon: LucideIcon; title: string; description: string; action: string }
> = {
    [InvitationStatus.Expired]: {
        icon: Clock,
        title: 'This invitation has expired',
        description:
            'Invitation links are time limited. Ask the shop to send a new one and it will work straight away.',
        action: 'Go to sign in',
    },
    [InvitationStatus.AlreadyUsed]: {
        icon: UserCheck,
        title: 'This invitation has already been used',
        description: 'You already have an account. Sign in with the password you set.',
        action: 'Sign in',
    },
    [InvitationStatus.Malformed]: {
        icon: CircleAlert,
        title: "This invitation link isn't complete",
        description:
            'The link looks cut short. Open it directly from the invitation email rather than copying it, or ask the shop to resend.',
        action: 'Go to sign in',
    },
    [InvitationStatus.Unknown]: {
        icon: CircleAlert,
        title: "This invitation can't be used",
        description: 'Ask the shop to send you a new invitation.',
        action: 'Go to sign in',
    },
};

export default function AcceptInvitationVerify() {
    const auth = useAuth();
    const navigate = useNavigate();
    const params = useParams();
    const [error, setError] = useState<any>(null);
    const [status, setStatus] = useState<InvitationStatus | undefined>(undefined);

    useEffect(() => {
        if (auth?.isLoggedIn) {
            auth.logout();
        }
    }, []);

    useEffect(() => {
        if (!auth?.isLoggedIn) {
            auth?.validateInvitation(params.code!)
                .then((result) => {
                    setStatus(result);
                })
                .catch((error) => {
                    setError(error);
                });
        }
    }, [auth?.isLoggedIn]);

    useEffect(() => {
        if (status === InvitationStatus.Valid) {
            navigate('/accept-invitation');
        }
    }, [status]);

    if (auth?.isLoggedIn) return <BlankLoadingPage message='Logging out of existing session...' />;
    // A thrown error is a failure to ask, not a verdict on the invitation, so
    // it keeps the generic error page.
    if (error) return <ErrorPage title="This invitation can't be used" message={INVITATION_REMEDY} link={{ label: "Go to sign in", url: "/login" }} />;

    if (status && status !== InvitationStatus.Valid) {
        const outcome = OUTCOMES[status];
        return (
            <div className="flex min-h-screen items-center justify-center bg-surface px-4 py-12">
                <ResultCard
                    tone="warning"
                    icon={outcome.icon}
                    title={outcome.title}
                    description={outcome.description}
                    action={{ label: outcome.action, href: '/login' }}
                    testId={`invitation-${status.toLowerCase()}`}
                />
            </div>
        );
    }

    return <BlankLoadingPage message='Checking your invitation…' />;
}
